import AddReactionRoundedIcon from '@mui/icons-material/AddReactionRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CopyrightRoundedIcon from '@mui/icons-material/CopyrightRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EmojiPeopleRoundedIcon from '@mui/icons-material/EmojiPeopleRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WatchLaterRoundedIcon from '@mui/icons-material/WatchLaterRounded';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/joy';
import AspectRatio from '@mui/joy/AspectRatio';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '../../components/confirmationModal';
import useEvent from '../../hooks/useEvent';
import { useUser } from '../../hooks/useUser';
import { joinEvent, leaveEvent } from '../../redux/actions/eventActions';
import { deleteEventService } from '../../redux/services/eventsService';
import { EventForm } from '../events/modal/form';
import convertJsonToHtml from '../../utils/convertJsonToHtml';

const EVENT_FALLBACK_IMAGE_URL =
  'https://fijnesnuiters.nl/wp-content/uploads/2020/12/Schools-cool-logo.png';

type EventInfoCardProps = {
  eventID: string;
};

export const EventInfoCard = (props: EventInfoCardProps) => {
  const eventQuery = useEvent(props.eventID);
  const userQuery = useUser();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const navigate = useNavigate();

  if (!eventQuery.isSuccess || !userQuery.isSuccess) {
    return null;
  }

  const follow = async () => {
    try {
      setLoading(true);
      await joinEvent(props.eventID);
      await eventQuery.refetch();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const unfollow = async () => {
    try {
      setLoading(true);
      await leaveEvent(props.eventID);
      await eventQuery.refetch();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirmation = () => {
    setOpenDeleteModal(true);
  };
  const onConfirmDelete = async () => {
    await deleteEventService(props.eventID);
    navigate('/events');
  };
  const closeDeleteConfirmation = () => {
    setOpenDeleteModal(false);
  };

  const event = eventQuery.data;
  const user = userQuery.data;

  const isFollowing = event.attendees.some(
    (member: any) => member._id === user._id
  );

  const isAdmin =
    user.role.toLowerCase() === 'admin' ||
    user.role.toLowerCase() === 'super_admin';

  const startTime = dayjs(event?.dateStart).format('HH:mm');
  const endTime = event?.dateEnd ? dayjs(event.dateEnd).format('HH:mm') : null;
  const isEventFull =
    event.attendees.length >= event.maxAttendees && event.maxAttendees != -1;
  const hasEvenStarted = dayjs(event.dateStart).isBefore(dayjs());

  return (
    <>
      <ConfirmationModal
        description="Weet je zeker dat je dit evenement wilt verwijderen?"
        title="Evenement verwijderen"
        open={openDeleteModal}
        onClose={closeDeleteConfirmation}
        onConfirm={onConfirmDelete}
        onCancel={closeDeleteConfirmation}
      />

      <EventForm
        open={editing}
        setOpen={(value: boolean) => {
          setEditing(value);
          eventQuery.refetch();
        }}
        event={event}
      />
      <Card variant="outlined">
        <Box
          display={{
            xs: 'block',
            md: 'none',
          }}
        >
          <Stack>
            <Typography level="title-lg">{event.title}</Typography>
            <Typography level="body-sm">
              {convertJsonToHtml(event.description)}
            </Typography>
          </Stack>
        </Box>
        <AspectRatio ratio="4/3" maxHeight="30vh">
          <img
            src={event.imageUrl || EVENT_FALLBACK_IMAGE_URL}
            loading="lazy"
            alt=""
            style={{ objectFit: 'contain' }}
          />
        </AspectRatio>
        <CardContent orientation="vertical">
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                <Chip
                  startDecorator={<EmojiPeopleRoundedIcon />}
                  size="sm"
                  sx={{
                    minWidth: 0,
                    height: 'auto',
                    '& .MuiChip-label': {
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  {event.attendees.length} deelnemers
                </Chip>

                <Chip
                  startDecorator={<CopyrightRoundedIcon />}
                  size="sm"
                  sx={{
                    minWidth: 0,
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    },
                  }}
                >
                  Beheerder: {event.owner.firstName}
                </Chip>

                <Chip
                  startDecorator={<CalendarTodayRoundedIcon />}
                  size="sm"
                  sx={{
                    minWidth: 0,
                    height: 'auto',
                    '& .MuiChip-label': {
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  {event.dateEnd
                    ? `${new Date(
                        event.dateStart
                      ).toLocaleDateString()} - ${new Date(
                        event.dateEnd
                      ).toLocaleDateString()}`
                    : `Datum: ${new Date(
                        event.dateStart
                      ).toLocaleDateString()}`}
                </Chip>

                {startTime && (
                  <Chip
                    startDecorator={<WatchLaterRoundedIcon />}
                    size="sm"
                    sx={{
                      minWidth: 0,
                      height: 'auto',
                      '& .MuiChip-label': {
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  >
                    {endTime
                      ? `${startTime} - ${endTime}`
                      : `Tijd: ${startTime}`}
                  </Chip>
                )}

                <Chip
                  startDecorator={<LocationCityRoundedIcon />}
                  size="sm"
                  sx={{
                    minWidth: 0,
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    },
                  }}
                >
                  Locatie: {event.location}
                </Chip>
              </Stack>
            </Box>

            <Stack spacing={1}>
              {isFollowing ? (
                <Button
                  onClick={unfollow}
                  loading={loading}
                  startDecorator={<LogoutRoundedIcon />}
                  fullWidth
                  disabled={hasEvenStarted}
                >
                  Verlaten
                </Button>
              ) : (
                <Button
                  onClick={follow}
                  loading={loading}
                  startDecorator={<AddReactionRoundedIcon />}
                  disabled={isEventFull || hasEvenStarted}
                  fullWidth
                >
                  Deelnemen
                </Button>
              )}
              {isAdmin && (
                <Stack direction="row" gap={1}>
                  <IconButton
                    onClick={() => setEditing(true)}
                    loading={loading}
                    color="neutral"
                    variant="soft"
                  >
                    <EditRoundedIcon />
                  </IconButton>
                  <IconButton
                    color="neutral"
                    loading={loading}
                    variant="soft"
                    onClick={openDeleteConfirmation}
                  >
                    <DeleteRoundedIcon />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};
